/**
 * UnitTooltip — R232q21 Phase 2.
 *
 * Universal hover tooltip for any unit (player or NPC mob). Renders a
 * compact Sirus-styled card anchored to the right of an arbitrary DOM
 * rect (typically the cursor or the element being hovered).
 *
 * Consumers (PlayerFrame, TargetFrame, BuffBar, nameplates …) own the
 * "is hover active?" state — this component is a pure render of:
 *   props.show + props.anchorRect + props.unitId
 * It looks up the unit row from `useNet()` (mobs first, then players)
 * and bails to `null` when the unit is unknown OR the tooltip is hidden
 * AND there is no anchor rect to fade out from.
 *
 * Visuals:
 *   – name in Cinzel, level + numeric HP in monospace;
 *   – classification badge using --aiwari-quality-* tokens;
 *   – faction-reaction icon (▼ hostile / ◆ neutral / ▲ friendly);
 *   – 100ms opacity fade so flicking the cursor between targets stays smooth.
 *
 * z-index 60 — above PlayerFrame (10) / TargetFrame (10) but below modals.
 *
 * TODO (later task):
 *   – mob.name field is missing from the schema; right now we render the
 *     `kind` slug ("wolf-grey") as a placeholder. Once NpcRegistry-style
 *     display names land on MobView, swap to `mob.displayName ?? kind`.
 *   – Faction reaction is a coarse self-vs-target side check; replace
 *     with a proper reputation lookup once rep tables are wired up.
 */

import type { CSSProperties } from 'react';
import { SirusFrame } from './SirusFrame';
import { useNet, type RemotePlayer, type MobView } from '../../state/net-store';

export interface UnitTooltipProps {
  unitId: string | null;
  show: boolean;
  anchorRect: DOMRect | null;
  className?: string;
}

type Reaction = 'friendly' | 'neutral' | 'hostile';
type Classification = 'normal' | 'elite' | 'rare' | 'boss';

interface UnitView {
  id: string;
  name: string;
  level: number;
  hp: number;
  hpMax: number;
  reaction: Reaction;
  classification: Classification;
  isPlayer: boolean;
}

const CLASSIFICATION_LABEL: Record<Classification, string> = {
  normal: 'Обычный',
  elite: 'Элита',
  rare: 'Редкий',
  boss: 'Босс',
};

const CLASSIFICATION_COLOUR: Record<Classification, string> = {
  normal: 'var(--aiwari-quality-common)',
  elite: 'var(--aiwari-quality-uncommon)',
  rare: 'var(--aiwari-quality-rare)',
  boss: 'var(--aiwari-quality-legendary)',
};

const REACTION_LABEL: Record<Reaction, string> = {
  friendly: 'Дружелюбный',
  neutral: 'Нейтральный',
  hostile: 'Враждебный',
};

const REACTION_COLOUR: Record<Reaction, string> = {
  friendly: 'hsl(120 60% 55%)',
  neutral: 'hsl(50 90% 55%)',
  hostile: 'hsl(0 70% 55%)',
};

const REACTION_GLYPH: Record<Reaction, string> = {
  friendly: '▲',
  neutral: '◆',
  hostile: '▼',
};

/** Format a slug like "wolf-grey" → "Wolf Grey" → "Волк-серый" placeholder. */
function prettyMobKind(kind: string): string {
  if (!kind) return 'Существо';
  return kind
    .split(/[-_]/g)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

/**
 * Coarse classification heuristic: the schema doesn't carry an explicit
 * Elite/Boss flag yet (TODO above), so we infer from `kind` substrings.
 * Falls back to 'normal' for anything we don't recognise.
 */
function classifyMob(mob: MobView): Classification {
  const k = (mob.kind || '').toLowerCase();
  if (k.includes('boss')) return 'boss';
  if (k.includes('rare')) return 'rare';
  if (k.includes('elite')) return 'elite';
  return 'normal';
}

/**
 * Compute friendly/neutral/hostile against the local player. Mob `kind`
 * strings beginning with "critter" / "ambient" → neutral; everything else
 * is hostile until proven otherwise. For players, same-side = friendly.
 */
function computeReaction(opts: {
  selfPlayer: RemotePlayer | null;
  target: RemotePlayer | MobView;
  isPlayer: boolean;
}): Reaction {
  const { selfPlayer, target, isPlayer } = opts;
  if (isPlayer) {
    if (!selfPlayer) return 'neutral';
    const targetSide = (target as RemotePlayer).side;
    if (targetSide && targetSide === selfPlayer.side) return 'friendly';
    return 'hostile';
  }
  const k = ((target as MobView).kind || '').toLowerCase();
  if (k.includes('critter') || k.includes('ambient') || k.includes('cow') || k.includes('rabbit')) {
    return 'neutral';
  }
  return 'hostile';
}

/** Resolve the unit row + normalised view-model. Returns null if unknown. */
function resolveUnit(
  unitId: string,
  players: Record<string, RemotePlayer>,
  mobs: Record<string, MobView>,
  selfPlayer: RemotePlayer | null,
): UnitView | null {
  // Convention: ids prefixed with "mob:" land in the mobs map first.
  if (unitId.startsWith('mob:')) {
    const m = mobs[unitId];
    if (m) {
      return {
        id: m.id,
        name: prettyMobKind(m.kind),
        level: m.level,
        hp: m.hp,
        hpMax: Math.max(1, m.hpMax),
        classification: classifyMob(m),
        reaction: computeReaction({ selfPlayer, target: m, isPlayer: false }),
        isPlayer: false,
      };
    }
  }
  const p = players[unitId];
  if (p) {
    return {
      id: p.id,
      name: p.name || 'Безымянный',
      level: p.level,
      hp: p.hp,
      hpMax: Math.max(1, p.hpMax),
      classification: 'normal',
      reaction: computeReaction({ selfPlayer, target: p, isPlayer: true }),
      isPlayer: true,
    };
  }
  const m = mobs[unitId];
  if (m) {
    return {
      id: m.id,
      name: prettyMobKind(m.kind),
      level: m.level,
      hp: m.hp,
      hpMax: Math.max(1, m.hpMax),
      classification: classifyMob(m),
      reaction: computeReaction({ selfPlayer, target: m, isPlayer: false }),
      isPlayer: false,
    };
  }
  return null;
}

export function UnitTooltip({ unitId, show, anchorRect, className }: UnitTooltipProps) {
  const players = useNet((s) => s.players);
  const mobs = useNet((s) => s.mobs);
  const selfId = useNet((s) => s.selfId);
  const selfPlayer = selfId ? players[selfId] ?? null : null;

  if (!unitId || !show || !anchorRect) return null;
  const unit = resolveUnit(unitId, players, mobs, selfPlayer);
  if (!unit) return null;

  // Anchor: 12px to the right of the rect, vertically aligned to its top.
  const left = Math.min(anchorRect.right + 12, window.innerWidth - 280);
  const top = Math.max(8, Math.min(anchorRect.top, window.innerHeight - 160));

  const wrapStyle: CSSProperties = {
    position: 'fixed',
    left,
    top,
    zIndex: 60,
    width: 240,
    pointerEvents: 'none',
    opacity: show ? 1 : 0,
    transition: 'opacity 100ms linear',
  };

  const hpPct = Math.max(0, Math.min(1, unit.hp / unit.hpMax));
  const cls = unit.classification;
  const reaction = unit.reaction;

  return (
    <div className={className} style={wrapStyle} role="tooltip" aria-hidden={!show}>
      <SirusFrame size="sm">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span
            style={{
              color: REACTION_COLOUR[reaction],
              fontSize: 12,
              lineHeight: 1,
              fontFamily: 'var(--aiwari-font-mono)',
            }}
            title={REACTION_LABEL[reaction]}
            aria-label={REACTION_LABEL[reaction]}
          >
            {REACTION_GLYPH[reaction]}
          </span>
          <span
            style={{
              fontFamily: 'var(--aiwari-font-display)',
              fontSize: 14,
              color: CLASSIFICATION_COLOUR[cls],
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {unit.name}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: 'var(--aiwari-font-mono)',
            fontSize: 11,
            color: 'var(--aiwari-fg-muted)',
            marginBottom: 6,
          }}
        >
          <span>Ур. {unit.level}</span>
          <span style={{ color: CLASSIFICATION_COLOUR[cls] }}>
            {CLASSIFICATION_LABEL[cls]}
          </span>
        </div>
        <div className="aiwari-bar" style={{ height: 12 }}>
          <div
            className="aiwari-bar-fill"
            style={{
              width: `${hpPct * 100}%`,
              background: 'var(--aiwari-bar-hp)',
            }}
          />
          <span className="aiwari-bar-text" style={{ lineHeight: '12px', fontSize: 10 }}>
            {Math.max(0, Math.round(unit.hp))} / {Math.round(unit.hpMax)}
          </span>
        </div>
        {unit.isPlayer ? null : (
          <div
            style={{
              marginTop: 6,
              fontFamily: 'var(--aiwari-font-body)',
              fontSize: 11,
              color: 'var(--aiwari-fg-dim)',
              fontStyle: 'italic',
            }}
          >
            Существо
          </div>
        )}
      </SirusFrame>
    </div>
  );
}
